import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Shield, ChevronDown, ChevronRight, CheckCircle2, AlertTriangle, XCircle, HelpCircle, Pencil, Loader2, BookOpen, Users, Award, TrendingUp } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const STATUS_CFG = {
  conforme: { label: "Conforme", color: "#22c55e", bg: "bg-green-500/10", icon: CheckCircle2 },
  partiel: { label: "Partiel", color: "#f59e0b", bg: "bg-yellow-500/10", icon: AlertTriangle },
  non_conforme: { label: "Non conforme", color: "#ef4444", bg: "bg-red-500/10", icon: XCircle },
};

const PHASE_CFG = [
  { id: "avant", label: "AVANT LA FORMATION", icon: BookOpen, color: "#3b82f6", bg: "from-blue-900/20 to-blue-900/5" },
  { id: "pendant", label: "PENDANT LA FORMATION", icon: Users, color: "#8b5cf6", bg: "from-purple-900/20 to-purple-900/5" },
  { id: "apres", label: "APRES LA FORMATION", icon: Award, color: "#10b981", bg: "from-green-900/20 to-green-900/5" },
  { id: "amelioration", label: "AMELIORATION CONTINUE", icon: TrendingUp, color: "#f7c935", bg: "from-yellow-900/20 to-yellow-900/5" },
];

const QUALIOPI_ROADMAP = [
  {
    phase: "avant",
    criteria: [
      {
        id: "C1", title: "Conditions d'information du public",
        indicators: [
          { id: "I1", label: "Diffusion d'une information accessible sur les prestations, delais d'acces et resultats obtenus", actions: ["Creer un site internet avec les programmes et CGV", "Mettre en ligne les indicateurs de resultats (taux satisfaction, reussite)", "Afficher les delais d'acces aux formations"] },
          { id: "I2", label: "Diffusion d'indicateurs de resultats adaptes", actions: ["Collecter les donnees : taux satisfaction, reussite, abandon", "Publier les resultats sur le site et les supports commerciaux", "Actualiser annuellement"] },
          { id: "I3", label: "Information du public sur les tarifs et modalites de financement", actions: ["Etablir une grille tarifaire accessible", "Informer sur les possibilites de prise en charge (OPCO, CPF, etc.)"] },
        ]
      },
      {
        id: "C2", title: "Identification des objectifs et adaptation",
        indicators: [
          { id: "I4", label: "Analyse du besoin du beneficiaire", actions: ["Mettre en place un questionnaire de recueil des besoins", "Realiser un entretien prealable avec chaque beneficiaire", "Formaliser l'analyse dans un document signe"] },
          { id: "I5", label: "Definition des objectifs operationnels et evaluables", actions: ["Rediger les objectifs pedagogiques en termes de competences mesurables", "Utiliser la taxonomie de Bloom pour structurer les objectifs"] },
          { id: "I6", label: "Etablissement des contenus et modalites de prestations", actions: ["Creer des programmes detailles (duree, contenu, methodes)", "Adapter les supports aux differentes modalites (presentiel, distanciel, mixte)"] },
          { id: "I7", label: "Prerequis et positionnement", actions: ["Definir les prerequis pour chaque formation", "Mettre en place un outil de positionnement (test, entretien)", "Adapter le parcours si necessaire"] },
        ]
      },
      {
        id: "C3-A", title: "Adaptation aux publics (Avant)",
        indicators: [
          { id: "I8", label: "Procedures d'accueil et d'evaluation de l'atteinte des objectifs", actions: ["Elaborer un livret d'accueil complet", "Definir les modalites d'evaluation (formative, sommative)", "Communiquer le planning et les conditions de deroulement"] },
          { id: "I9", label: "Information communiquee sur les conditions de deroulement", actions: ["Envoyer une convocation avec toutes les informations pratiques", "Preciser les moyens mis a disposition (salles, materiel, outils numeriques)"] },
          { id: "I26", label: "Prise en compte des situations de handicap", actions: ["Nommer un referent handicap", "Adapter les conditions d'acces et les supports", "Recenser les solutions de compensation disponibles"] },
        ]
      },
      {
        id: "C5-A", title: "Qualification et developpement des competences (Avant)",
        indicators: [
          { id: "I17", label: "Veille legale et reglementaire", actions: ["Suivre les evolutions legislatives du secteur formation", "Integrer les nouvelles obligations dans les pratiques", "Constituer un dossier de veille reglementaire"] },
        ]
      },
    ]
  },
  {
    phase: "pendant",
    criteria: [
      {
        id: "C4", title: "Adequation des moyens pedagogiques et techniques",
        indicators: [
          { id: "I12", label: "Description et adaptation des moyens pedagogiques et techniques", actions: ["Inventorier les moyens materiels et pedagogiques", "S'assurer de l'adequation avec les objectifs", "Utiliser des methodes variees et interactives"] },
          { id: "I13", label: "Coordination des intervenants internes ou externes", actions: ["Etablir un tableau de coordination des intervenants", "Organiser des reunions de cadrage regulieres", "Verifier les qualifications des intervenants"] },
          { id: "I14", label: "Mise a disposition des ressources pedagogiques", actions: ["Constituer une base documentaire a jour", "Assurer l'accessibilite numerique des contenus", "Mettre a jour regulierement les ressources"] },
        ]
      },
      {
        id: "C3-B", title: "Accompagnement et suivi (Pendant)",
        indicators: [
          { id: "I10", label: "Adaptation des prestations au handicap", actions: ["Appliquer les adaptations identifiees en amont", "Evaluer l'efficacite des amenagements", "Ajuster si necessaire en cours de formation"] },
          { id: "I11", label: "Evaluation de l'atteinte des objectifs en cours", actions: ["Realiser des evaluations formatives regulieres", "Ajuster le rythme et les contenus selon les resultats", "Mettre en place des temps d'echange avec les stagiaires"] },
        ]
      },
    ]
  },
  {
    phase: "apres",
    criteria: [
      {
        id: "C7-A", title: "Recueil des appreciations",
        indicators: [
          { id: "I30", label: "Recueil des appreciations des parties prenantes", actions: ["Diffuser un questionnaire de satisfaction a chaud", "Analyser les resultats et identifier les axes d'amelioration", "Communiquer les resultats aux beneficiaires et financeurs"] },
          { id: "I31", label: "Traitement des difficultes rencontrees", actions: ["Mettre en place une procedure de gestion des reclamations", "Tracer toutes les reclamations et leur traitement", "Informer le reclamant de la suite donnee"] },
        ]
      },
      {
        id: "C5-B", title: "Developpement des competences (Apres)",
        indicators: [
          { id: "I19", label: "Developpement des competences des salaries", actions: ["Etablir un plan de developpement des competences", "Suivre les formations des intervenants", "Evaluer l'impact sur la qualite des prestations"] },
          { id: "I16", label: "Competences des intervenants justifiees", actions: ["Tenir a jour les CV et diplomes des intervenants", "Verifier l'adequation profil / intervention", "Constituer un dossier par intervenant"] },
        ]
      },
      {
        id: "C3-C", title: "Suivi post-formation",
        indicators: [
          { id: "I2B", label: "Evaluation a froid des resultats", actions: ["Envoyer un questionnaire a froid (3 a 6 mois apres)", "Evaluer le transfert des competences en situation de travail", "Analyser l'impact de la formation sur l'activite"] },
        ]
      },
    ]
  },
  {
    phase: "amelioration",
    criteria: [
      {
        id: "C6", title: "Inscription dans l'environnement professionnel",
        indicators: [
          { id: "I18", label: "Veille sur les evolutions des competences, metiers et emplois", actions: ["Participer a des evenements sectoriels", "Suivre les publications des branches professionnelles", "Adapter l'offre de formation aux evolutions du marche"] },
          { id: "I20", label: "Actions de veille sectorielle", actions: ["Constituer un comite de veille", "Documenter les analyses et decisions", "Integrer les resultats dans la strategie pedagogique"] },
          { id: "I21", label: "Exploitation des retours des parties prenantes", actions: ["Analyser les retours des financeurs, entreprises, stagiaires", "Identifier les actions correctives necessaires", "Mettre en oeuvre les ameliorations"] },
          { id: "I22", label: "Conformite reglementaire", actions: ["Verifier la conformite de tous les documents", "Mettre a jour les CGV, reglement interieur", "Assurer le suivi des obligations legales (RGPD, etc.)"] },
        ]
      },
      {
        id: "C7-B", title: "Amelioration par les retours et mesures correctives",
        indicators: [
          { id: "I23", label: "Recueil des appreciations de toutes les parties prenantes", actions: ["Diversifier les modalites de recueil (enquetes, entretiens, reunions)", "Analyser les tendances et les signaux faibles", "Exploiter les donnees pour l'amelioration continue"] },
          { id: "I24", label: "Traitement des reclamations et alea", actions: ["Formaliser une procedure de gestion des aleas", "Documenter chaque incident et sa resolution", "Mettre en place des actions preventives"] },
          { id: "I25", label: "Mesures d'amelioration continue", actions: ["Realiser une revue annuelle du systeme qualite", "Definir un plan d'amelioration avec objectifs mesurables", "Suivre les indicateurs de performance"] },
          { id: "I32", label: "Mesures d'amelioration issues de l'analyse des appreciations", actions: ["Etablir un plan d'actions correctives", "Prioriser les ameliorations par impact", "Suivre la mise en oeuvre et l'efficacite"] },
        ]
      },
    ]
  },
];

export default function QualiopiPage() {
  const { axiosAuth } = useAuth();
  const { t } = useTranslation();
  const api = axiosAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openPhases, setOpenPhases] = useState({ avant: true });
  const [openCriteria, setOpenCriteria] = useState({});
  const [showEvalDialog, setShowEvalDialog] = useState(false);
  const [evalTarget, setEvalTarget] = useState(null);
  const [evalForm, setEvalForm] = useState({ status: "conforme", notes: "", evidence: "", action_plan: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const s = await api.get("/qualiopi/status");
      setStatus(s.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const togglePhase = (id) => setOpenPhases(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleCriteria = (id) => setOpenCriteria(prev => ({ ...prev, [id]: !prev[id] }));

  const openEval = (indicator) => {
    setEvalTarget(indicator);
    const existing = status?.indicator_status?.[indicator.id];
    setEvalForm(existing ? { status: existing.status, notes: existing.notes || "", evidence: existing.evidence || "", action_plan: existing.action_plan || "" } : { status: "conforme", notes: "", evidence: "", action_plan: "" });
    setShowEvalDialog(true);
  };

  const handleSaveEval = async () => {
    if (!evalTarget) return;
    setSaving(true);
    try {
      await api.post("/qualiopi/evaluate", { indicator_id: evalTarget.id, ...evalForm });
      setShowEvalDialog(false);
      await load();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const getIndicatorStatus = (indicatorId) => status?.indicator_status?.[indicatorId];

  // Count all indicators in the roadmap
  const allIndicators = QUALIOPI_ROADMAP.flatMap(p => p.criteria.flatMap(c => c.indicators));
  const totalCount = allIndicators.length;
  const conformeCount = allIndicators.filter(ind => getIndicatorStatus(ind.id)?.status === "conforme").length;
  const partielCount = allIndicators.filter(ind => getIndicatorStatus(ind.id)?.status === "partiel").length;
  const ncCount = allIndicators.filter(ind => getIndicatorStatus(ind.id)?.status === "non_conforme").length;
  const notEvaluated = totalCount - conformeCount - partielCount - ncCount;
  const completionPct = totalCount > 0 ? Math.round((conformeCount / totalCount) * 100) : 0;

  if (loading) return <div className="p-8 text-slate-400">Chargement...</div>;

  return (
    <div className="p-4 md:p-8 max-w-5xl" data-testid="qualiopi-page">
      <PageHeader
        icon={<Shield />}
        title={t("qualiopi.title")}
        description={t("qualiopi.subtitle")}
        testId="qualiopi-header"
      />

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        {[
          { label: t("qualiopi.indicators"), value: totalCount, color: "#f7c935" },
          { label: t("qualiopi.conforme"), value: conformeCount, color: "#22c55e" },
          { label: t("qualiopi.partiel"), value: partielCount, color: "#f59e0b" },
          { label: t("qualiopi.non_conforme"), value: ncCount, color: "#ef4444" },
          { label: t("qualiopi.non_evalue"), value: notEvaluated, color: "#64748b" },
        ].map((s, i) => (
          <div key={i} className="bg-[#14101c] border border-white/10 rounded-xl p-4 animate-fade-in-up" style={{ animationDelay: `${i * 0.05}s` }}>
            <div className="text-2xl font-black" style={{ color: s.color }}>{s.value}</div>
            <div className="text-xs text-slate-500">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Global progress */}
      <div className="bg-[#14101c] border border-white/10 rounded-xl p-6 mb-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold flex items-center gap-2"><Shield className="w-5 h-5" style={{ color: "#f7c935" }} /> Progression globale</h3>
          <span className="text-2xl font-black" style={{ color: completionPct >= 80 ? "#22c55e" : completionPct >= 50 ? "#f59e0b" : "#ef4444" }}>{completionPct}%</span>
        </div>
        <div className="h-3 rounded-full bg-white/5 overflow-hidden">
          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${completionPct}%`, background: completionPct >= 80 ? "#22c55e" : completionPct >= 50 ? "#f59e0b" : "#ef4444" }} />
        </div>
        {/* Phase progress */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          {QUALIOPI_ROADMAP.map(phase => {
            const cfg = PHASE_CFG.find(p => p.id === phase.phase);
            const phaseInds = phase.criteria.flatMap(c => c.indicators);
            const pConf = phaseInds.filter(ind => getIndicatorStatus(ind.id)?.status === "conforme").length;
            const pPct = phaseInds.length > 0 ? Math.round((pConf / phaseInds.length) * 100) : 0;
            return (
              <div key={phase.phase} className="bg-[#0b0811] rounded-lg p-3">
                <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: cfg?.color }}>{cfg?.label}</div>
                <div className="flex items-center gap-2 mt-1">
                  <div className="text-lg font-black" style={{ color: cfg?.color }}>{pPct}%</div>
                  <div className="flex-1 h-1.5 rounded-full bg-white/5"><div className="h-full rounded-full" style={{ width: `${pPct}%`, background: cfg?.color }} /></div>
                </div>
                <div className="text-[10px] text-slate-600 mt-0.5">{pConf}/{phaseInds.length}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Phases accordion */}
      <div className="space-y-4">
        {QUALIOPI_ROADMAP.map(phase => {
          const cfg = PHASE_CFG.find(p => p.id === phase.phase);
          const PhaseIcon = cfg?.icon || BookOpen;
          const isPhaseOpen = openPhases[phase.phase];

          return (
            <div key={phase.phase} className="overflow-hidden rounded-xl border border-white/10" style={{ background: "#14101c" }} data-testid={`phase-${phase.phase}`}>
              <button onClick={() => togglePhase(phase.phase)}
                className={`w-full flex items-center gap-3 p-5 text-left transition-colors bg-gradient-to-r ${cfg?.bg}`}>
                {isPhaseOpen ? <ChevronDown className="w-5 h-5" style={{ color: cfg?.color }} /> : <ChevronRight className="w-5 h-5" style={{ color: cfg?.color }} />}
                <PhaseIcon className="w-6 h-6" style={{ color: cfg?.color }} />
                <div className="flex-1">
                  <div className="text-sm font-black uppercase tracking-wider" style={{ color: cfg?.color }}>{cfg?.label}</div>
                  <div className="text-xs text-slate-500">{phase.criteria.length} critere(s) - {phase.criteria.flatMap(c => c.indicators).length} indicateur(s)</div>
                </div>
              </button>

              {isPhaseOpen && (
                <div className="p-4 space-y-3">
                  {phase.criteria.map(criterion => {
                    const isCritOpen = openCriteria[criterion.id];
                    const critConf = criterion.indicators.filter(ind => getIndicatorStatus(ind.id)?.status === "conforme").length;

                    return (
                      <div key={criterion.id} className="bg-[#0b0811] rounded-xl border border-white/5" data-testid={`criterion-${criterion.id}`}>
                        <button onClick={() => toggleCriteria(criterion.id)}
                          className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/[0.02] transition-colors">
                          {isCritOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ background: cfg?.color, color: "#14101c" }}>{criterion.id}</span>
                          <span className="text-sm font-bold flex-1">{criterion.title}</span>
                          <span className="text-xs font-medium" style={{ color: critConf === criterion.indicators.length ? "#22c55e" : "#f59e0b" }}>
                            {critConf}/{criterion.indicators.length}
                          </span>
                        </button>

                        {isCritOpen && (
                          <div className="px-4 pb-4 space-y-2">
                            {criterion.indicators.map(ind => {
                              const ev = getIndicatorStatus(ind.id);
                              const stCfg = ev ? STATUS_CFG[ev.status] : null;
                              const StatusIcon = stCfg?.icon || HelpCircle;
                              return (
                                <div key={ind.id} className="p-3 rounded-lg bg-[#14101c] border border-white/5 group" data-testid={`indicator-${ind.id}`}>
                                  <div className="flex items-center gap-3">
                                    <StatusIcon className="w-5 h-5 flex-shrink-0" style={{ color: stCfg?.color || "#64748b" }} />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="text-[10px] font-mono text-slate-500">{ind.id}</span>
                                        <span className="text-xs font-medium">{ind.label}</span>
                                      </div>
                                      {ev?.notes && <p className="text-[10px] text-slate-500 mt-0.5 truncate">{ev.notes}</p>}
                                    </div>
                                    {stCfg && <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${stCfg.bg}`} style={{ color: stCfg.color }}>{stCfg.label}</span>}
                                    <button onClick={() => openEval(ind)} className="p-1.5 rounded-lg hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`eval-${ind.id}`}>
                                      <Pencil className="w-3.5 h-3.5 text-slate-400" />
                                    </button>
                                  </div>
                                  {/* Actions for this indicator */}
                                  {ind.actions && ind.actions.length > 0 && (
                                    <div className="mt-2 ml-8 space-y-0.5">
                                      {ind.actions.map((action, ai) => (
                                        <div key={ai} className="text-[10px] text-slate-600 flex items-start gap-1.5">
                                          <span className="w-1 h-1 rounded-full mt-1 flex-shrink-0" style={{ background: stCfg?.color || "#64748b" }} />
                                          <span>{action}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Evaluation Dialog */}
      <Dialog open={showEvalDialog} onOpenChange={setShowEvalDialog}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Evaluer {evalTarget?.id} - {evalTarget?.label?.substring(0, 60)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-400 mb-2 block">Statut</label>
              <div className="flex gap-2">
                {Object.entries(STATUS_CFG).map(([key, cfg]) => {
                  const Icon = cfg.icon;
                  return (
                    <button key={key} onClick={() => setEvalForm(prev => ({ ...prev, status: key }))}
                      className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border transition-colors ${evalForm.status === key ? "border-[#f7c935]/50 bg-[#f7c935]/10" : "border-white/10 hover:border-white/20"}`}
                      data-testid={`eval-status-${key}`}>
                      <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                      <span className="text-xs font-medium" style={{ color: cfg.color }}>{cfg.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-400 mb-1 block">Notes</label>
              <Textarea value={evalForm.notes} onChange={e => setEvalForm(prev => ({ ...prev, notes: e.target.value }))} placeholder="Commentaires..." rows={2} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" data-testid="eval-notes" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-400 mb-1 block">Preuves / Documents</label>
              <Textarea value={evalForm.evidence} onChange={e => setEvalForm(prev => ({ ...prev, evidence: e.target.value }))} placeholder="References des preuves..." rows={2} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" data-testid="eval-evidence" />
            </div>
            {evalForm.status !== "conforme" && (
              <div>
                <label className="text-sm font-medium text-slate-400 mb-1 block">Plan d'action</label>
                <Textarea value={evalForm.action_plan} onChange={e => setEvalForm(prev => ({ ...prev, action_plan: e.target.value }))} placeholder="Actions correctives..." rows={2} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" data-testid="eval-action-plan" />
              </div>
            )}
            <Button onClick={handleSaveEval} disabled={saving} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="eval-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Enregistrer l'evaluation
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
